import { AudienceFilters } from '../constants/graphqlTypes';
import { GeoFilter } from '../contexts/FilterContext';
import { PersonFilter } from '../types/api.graphql';
import { createGeoFilterHelperFromGraphQL, createParentGeoFilterHelperFromGraphQL } from './filters';

// Cap on how many geo filters a user can stack on one audience. Shared by the audience-creation
// GeoFilters panel and the A2UI geo-filter modal — both hide their "add" button past it.
export const MAX_GEO_FILTERS_COUNT = 10;

// Mirrors MAX_SUBMISSION_VALUE_CHARS in packages/ai-insights-agent/src/a2ui/form_submissions.py.
// The A2UI geo-filter modal serializes its selection into a single submission field, which the
// agent rejects above this size, keep both constants in sync if either changes.
export const MAX_GEO_FILTER_SUBMISSION_CHARS = 4000;

export const maxZipAreaOpacity = 0.6;
export const minZipAreaOpacity = 0.1;
export const maxStateAreaOpacity = 0.6;
export const minStateAreaOpacity = 0.1;

export interface LegendRange {
  from: number;
  to: number;
  opacity: number;
}

export const zipOpacity = (zipCount: number, zipMaxCount: number) => {
  return minZipAreaOpacity + ((maxZipAreaOpacity - minZipAreaOpacity) * zipCount) / zipMaxCount;
};

export const getLegendRanges = (filters: GeoFilter[], rangesCount = 7): LegendRange[] => {
  const zipMaxCountForFilter = (filter: GeoFilter) =>
    filter.zipCodes ? Math.max(...filter.zipCodes.map((zipCode) => zipCode.count)) : 0;
  const zipMaxCount = Math.max(0, ...filters.map((filter) => zipMaxCountForFilter(filter)));
  const rangeLength = Math.trunc(zipMaxCount / rangesCount);
  const ranges: LegendRange[] = [];
  for (let i = 0; i < rangesCount; i++) {
    const from = i * rangeLength;
    const to = i < rangesCount - 1 ? from + rangeLength : zipMaxCount;
    const zipCount = (from + to) / 2;
    const opacity = zipOpacity(zipCount, zipMaxCount);
    ranges.push({ from, to, opacity });
  }
  return ranges;
};

export const getStateLegendRanges = (states, rangesCount = 7): LegendRange[] => {
  const statesMaxCountForFilter = (states) => Math.max(...(states.map((state) => state.count) as number[]));
  const stateMaxCount = Math.max(0, statesMaxCountForFilter(states));
  const rangeLength = Math.trunc(stateMaxCount / rangesCount);
  const ranges: LegendRange[] = [];
  for (let i = 0; i < rangesCount; i++) {
    const from = i * rangeLength;
    const to = i < rangesCount - 1 ? from + rangeLength : stateMaxCount;
    const zipCount = (from + to) / 2;
    const opacity = zipOpacity(zipCount, stateMaxCount);
    ranges.push({ from, to, opacity });
  }
  return ranges;
};

export const getGeoFiltersWithoutCounts = (filters: AudienceFilters): GeoFilter[] =>
  (filters || [])
    .filter((filter) => !!filter.geoFilter)
    .map((filter) => {
      return createGeoFilterHelperFromGraphQL(filter.geoFilter).fromGraphQLGeoFilter();
    });

export const getParentGeoFiltersForMap = (parentFiltersGraphQl: PersonFilter[], audienceFilters: GeoFilter[]) => {
  if (!parentFiltersGraphQl?.length) return [];

  const segmentGeoFilterIds = audienceFilters.map((geoFilter) => geoFilter.id);
  return (
    parentFiltersGraphQl
      .filter((filter) => !!filter.geoFilter)
      .map((filter) => createParentGeoFilterHelperFromGraphQL(filter.geoFilter, { zipCodes: [] }))
      // remove parent filter and show copied one only for excluded zips in the parent one
      .filter((filter) => !segmentGeoFilterIds.includes(filter.id))
  );
};
